// src/components/documents/DocumentEmpty.jsx - V1 ENTERPRISE
// ═══════════════════════════════════════════════════════════════════════════
// 📭 DOCUMENT EMPTY — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 ULTRA ROBUSTO:    Estado vacío con call to action claro
// 🔥 ULTRA RÁPIDO:     GPU-accelerated animation + React.memo
// 🔥 ULTRA EFICIENTE:  Componente mínimo sin dependencias pesadas
// 🔥 ULTRA PROFESIONAL: Ilustración contextual + mensaje orientado a acción
// ═══════════════════════════════════════════════════════════════════════════

import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { FileText, Plus, Upload } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 CONSTANTES DE ANIMACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const CONTAINER_ANIMATION = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
};

const ICON_ANIMATION = {
  initial: { scale: 0.8, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  transition: { delay: 0.1, duration: 0.4, ease: 'easeOut' },
};

// ═══════════════════════════════════════════════════════════════════════════
// 📭 COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const DocumentEmpty = memo(function DocumentEmpty({ isMobile, onNewDocument, hasFilters }) {
  return (
    <motion.div
      {...CONTAINER_ANIMATION}
      className="flex flex-col items-center justify-center text-center py-12 px-4"
    >
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ILUSTRACIÓN                                                     */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <motion.div
        {...ICON_ANIMATION}
        className={`
          inline-flex items-center justify-center rounded-3xl
          bg-slate-100 border border-slate-200
          ${isMobile ? 'w-16 h-16 mb-4' : 'w-20 h-20 mb-5'}
        `}
      >
        {hasFilters ? (
          <Upload
            size={isMobile ? 28 : 36}
            className="text-slate-400"
            strokeWidth={1.5}
          />
        ) : (
          <FileText
            size={isMobile ? 28 : 36}
            className="text-slate-400"
            strokeWidth={1.5}
          />
        )}
      </motion.div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* MENSAJE                                                         */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <h3
        className={`font-semibold text-slate-900 mb-1 ${
          isMobile ? 'text-base' : 'text-lg'
        }`}
      >
        {hasFilters ? 'Nenhum resultado encontrado' : 'Nenhum documento'}
      </h3>

      <p
        className={`text-slate-500 mb-6 max-w-sm ${
          isMobile ? 'text-xs' : 'text-sm'
        }`}
      >
        {hasFilters
          ? 'Tente ajustar os filtros ou limpar a busca para ver mais resultados.'
          : 'Comece enviando seu primeiro documento. Arraste arquivos ou clique no botão abaixo.'}
      </p>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CALL TO ACTION (solo si no hay filtros activos)                */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {!hasFilters && onNewDocument && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onNewDocument}
          className={`
            inline-flex items-center gap-2
            bg-blue-600 hover:bg-blue-700 active:bg-blue-800
            text-white font-semibold
            rounded-2xl shadow-lg shadow-blue-200
            transition-all duration-200
            ${isMobile ? 'px-5 py-3 text-sm' : 'px-6 py-3.5 text-base'}
          `}
        >
          <Plus size={isMobile ? 18 : 20} strokeWidth={2} />
          Novo Documento
        </motion.button>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SUGERENCIA SECUNDARIA (solo desktop, sin filtros)              */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {!hasFilters && !isMobile && (
        <p className="text-xs text-slate-400 mt-4">
          Ou pressione{' '}
          <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[11px] font-mono">
            Ctrl+N
          </kbd>{' '}
          para criar um novo documento
        </p>
      )}
    </motion.div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 📤 EXPORT
// ═══════════════════════════════════════════════════════════════════════════

DocumentEmpty.displayName = 'DocumentEmpty';
export default DocumentEmpty;